// A WEBSITE'S PAGES AND DESIGN SYSTEM IN AN AI ANSWER — the `project-pages`
// and `design-system` views (functions/previewAnswer.js previewAiViews;
// components/ProjectPagesView.js, PreviewDesignSystemView.js).
//
//   project-pages   the pages a site's data declares (or its components) as
//                   screenshot tiles — the screenshot CDN's capture of the
//                   environment (`screenshotCdnUrl`, shared functions/
//                   projects.js projectScreenshotUrl's address); a tile swaps
//                   in the LIVE thing: a page is the published page itself, a
//                   component the project's component in the sandboxed
//                   user-code frame (shared embedComponent).
//   design-system   the site's tokens, type and icons: the design-system
//                   showcase over the site's own data in the sandboxed
//                   user-code frame (shared embedDesignSystem — the embed the
//                   preview's own sidebar mounts).
//
// The site and its data are the site-preview view's (previewAnswerResolve,
// previewSiteRead — one read per site and environment for the whole page):
// the published data when the environment is published, else the editor's.
// Nothing here writes project data.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

// The data a view shows for a site: the published data, else (a private
// site, an environment not published) the editor's. `{ data, published }` —
// data null when neither answers.
export async function previewViewData(site) {
  const host = this
  const s = site && typeof site === 'object' ? site : null
  if (!s) return { data: null, published: null }
  const pub = await host.call('previewSiteRead', { owner: s.owner, key: s.key, env: s.env })
  if (pub && pub.data) return { data: pub.data, published: true }
  const auth = await host.call('previewSiteRead', {
    owner: s.owner,
    key: s.key,
    env: s.env,
    auth: true
  })
  return {
    data: (auth && auth.data) || null,
    published: pub && !pub.error ? pub.published : null
  }
}

// The tiles of a site: its pages (the addresses its data declares) or its
// components (the registered keys), each `{ key, kind, target, title,
// image }` — `image` the screenshot CDN's capture (a page's at its address,
// a component's by its key) for this environment, at a tile's width.
export async function previewPagesTiles(site, data, show) {
  const s = site && typeof site === 'object' ? site : null
  if (!s || !data) return []
  let channels = null
  try {
    channels = await import('@symbo.ls/channels')
  } catch (_) {
    channels = null
  }
  const shot = (extra) =>
    channels && typeof channels.screenshotCdnUrl === 'function'
      ? channels.screenshotCdnUrl({
          owner: s.owner,
          key: String(s.key).split('.')[0],
          env: s.env,
          width: 480,
          ...extra
        })
      : null
  if (show === 'components') {
    const comps = data.components && typeof data.components === 'object' ? data.components : {}
    return Object.keys(comps)
      .filter((k) => /^[A-Z][A-Za-z0-9_]*$/.test(k) && comps[k] && typeof comps[k] === 'object')
      .sort((a, b) => a.localeCompare(b))
      .map((k) => ({
        key: 'comp-' + k,
        kind: 'component',
        target: k,
        title: k,
        image: shot({ componentKey: k })
      }))
  }
  return this.call('previewAnswerPages', data).map((p, i) => ({
    key: 'page-' + i,
    kind: 'page',
    target: p,
    title: p,
    image: shot({ page: p })
  }))
}

// The project-pages view's read, once per inputs (its OWN state): the site,
// its tiles (the newest `limit`), how many there are.
export async function previewPagesLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const params = (host.scope && host.scope.params) || {}
  const show = host.state.show || (params.show === 'components' ? 'components' : 'pages')
  const limit = Math.max(1, Math.min(24, Math.floor(Number(params.limit)) || 12))
  host.call('canvasAnswerNamedMoved', host, params.project)
  const sig = host.call('previewAnswerInputs', host) + '|' + show + '|' + limit
  if (host.__previewPagesSig === sig) return null
  host.__previewPagesSig = sig
  const res = await host.call('previewAnswerResolve', host)
  if (host.__previewPagesSig !== sig) return null
  const site = res.site
  if (!site) {
    // The one sentence (previewAnswerEmptyText): a name that is no website
    // says what it is, with its name — never "no match".
    host.state.update({
      site: null,
      tiles: null,
      failed: true,
      none: !params.project && !!res.read && !res.read.failed && !res.sites.length,
      notWebsite: res.notWebsite || null,
      storeApp: res.storeApp || null
    })
    return null
  }
  host.state.update({
    site: { key: site.key, name: site.name, owner: site.owner, env: site.env, path: site.path },
    tiles: null,
    total: 0,
    show,
    failed: false,
    none: false,
    notWebsite: null,
    storeApp: null
  })
  // The tiles come from the data; what a page's ADDRESS serves (a switched-off
  // environment serves nothing) from the project's environments — the site
  // card's own read (previewSiteHosting, memoized for the page).
  const [view, hosting] = await Promise.all([
    host.call('previewViewData', site),
    host.call('previewSiteHosting', { owner: site.owner, key: site.key, id: site.id || null })
  ])
  if (host.__previewPagesSig !== sig) return null
  const tiles = view.data ? await host.call('previewPagesTiles', site, view.data, show) : []
  if (host.__previewPagesSig !== sig) return null
  host.state.update({
    tiles: tiles.slice(0, limit),
    total: tiles.length,
    published: view.published,
    serves: host.call('previewEnvServes', hosting, site.env),
    unread: !view.data
  })
  return tiles
}

// A tile's click: the live thing in place of the grid — a page is the
// published page at its address (the site-preview's frame), a component the
// project's component in the user-code frame. A page of a switched-off
// environment opens no frame: its address serves nothing, and the live note
// says so (previewPagesLiveNote). `row` is the tile's state.
export async function previewPagesOpen(el, row) {
  const view =
    el && el.dataProjectPages === true
      ? el
      : el && typeof el.lookup === 'function'
        ? el.lookup((e) => e.dataProjectPages === true)
        : null
  const s = view && view.state
  const t = row && typeof row === 'object' ? row : null
  if (!s || !t || !s.site || typeof s.update !== 'function') return false
  if (t.kind === 'page') {
    if (s.serves === 'off') {
      s.update({
        live: { kind: 'page', target: t.target, title: t.title, url: null },
        frameReady: null,
        frameSlow: null,
        liveError: 'off'
      })
      return true
    }
    const url = await view.call('previewAnswerUrl', s.site, t.target, s.site.env)
    if (!url) return false
    s.update({
      live: { kind: 'page', target: t.target, title: t.title, url },
      frameReady: null,
      frameSlow: null,
      liveError: null
    })
    return true
  }
  s.update({
    live: { kind: 'component', target: t.target, title: t.title, url: null },
    liveError: null,
    liveReady: false
  })
  const frameEl = view.lookdown('ProjectPagesComponentFrame')
  const node = frameEl && frameEl.node
  const data = await view.call('previewViewData', s.site)
  if (!node || !data.data) {
    s.update({ liveError: 'data' })
    return false
  }
  try {
    const out = await view.call('embedComponent', node, data.data, {
      key: t.target,
      fullsize: true,
      el: view
    })
    if (out && out.bootError) {
      s.update({ liveError: 'boot' })
      return false
    }
  } catch (_) {
    s.update({ liveError: 'boot' })
    return false
  }
  if (!s.live || s.live.target !== t.target) return false
  s.update({ liveReady: true })
  return true
}

// Back from the live thing to the tiles.
export function previewPagesBack(el) {
  const view =
    el && el.dataProjectPages === true
      ? el
      : el && typeof el.lookup === 'function'
        ? el.lookup((e) => e.dataProjectPages === true)
        : null
  const s = view && view.state
  if (!s || typeof s.update !== 'function' || !s.live) return false
  s.update({ live: null, liveReady: false, liveError: null, frameReady: null, frameSlow: null })
  return true
}

// The live thing's one quiet line, or '': loading, a slow page, a component
// that cannot mount, a page of a switched-off environment. `s` is the
// project-pages view's state.
export function previewPagesLiveNote(s) {
  const st = s && typeof s === 'object' ? s : {}
  const live = st.live
  if (!live) return ''
  const say = (key) => String(this.call('polyglot', key)).replace('{name}', () => live.title || '')
  if (live.kind === 'page' && st.liveError === 'off') {
    const site = st.site || {}
    return String(this.call('polyglot', 'preview.answer.envOff'))
      .replace('{name}', () => site.name || site.key || '')
      .replace('{env}', () => String(this.call('polyglot', this.call('previewEnvLabel', site.env))))
  }
  if (live.kind === 'component') {
    if (st.liveError) return say('preview.pagesView.componentFailed')
    return st.liveReady ? '' : say('preview.answer.loadingPart')
  }
  const frameKey = (live.url || '') + '#0'
  if (st.frameReady === frameKey) return ''
  return st.frameSlow === frameKey ? say('preview.answer.slow') : say('preview.answer.loadingPart')
}

// THE DESIGN-SYSTEM VIEW's read and mount (its OWN state): the site, then the
// showcase of the section (`colors`, `typography`, `spacing`, `icons`,
// `transitions`, `layout` — the preview sidebar's own pages) over the site's
// data in the user-code frame. A new section is a remount over the frame's
// port (embedDesignSystem's own contract).
export async function previewDsLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const params = (host.scope && host.scope.params) || {}
  const SECTIONS = ['colors', 'typography', 'spacing', 'icons', 'transitions', 'layout']
  const section = SECTIONS.includes(host.state.section)
    ? host.state.section
    : SECTIONS.includes(params.section)
      ? params.section
      : 'colors'
  host.call('canvasAnswerNamedMoved', host, params.project)
  const sig = host.call('previewAnswerInputs', host) + '|' + section
  if (host.__previewDsSig === sig) return null
  host.__previewDsSig = sig
  const res = await host.call('previewAnswerResolve', host)
  if (host.__previewDsSig !== sig) return null
  const site = res.site
  if (!site) {
    // The one sentence (previewAnswerEmptyText), as the pages card's.
    host.state.update({
      site: null,
      failed: true,
      none: !params.project && !!res.read && !res.read.failed && !res.sites.length,
      notWebsite: res.notWebsite || null,
      storeApp: res.storeApp || null
    })
    return null
  }
  const same =
    host.state.site && host.state.site.key === site.key && host.state.site.env === site.env
  host.state.update({
    site: { key: site.key, name: site.name, owner: site.owner, env: site.env },
    current: section,
    failed: false,
    none: false,
    notWebsite: null,
    storeApp: null,
    ...(same ? {} : { ready: false }),
    error: null
  })
  const frameEl = host.lookdown('PreviewDsFrame')
  const node = frameEl && frameEl.node
  const view = await host.call('previewViewData', site)
  if (host.__previewDsSig !== sig) return null
  if (!node || !view.data) {
    host.state.update({ error: 'data' })
    return null
  }
  const ds = view.data.designSystem
  if (!ds || typeof ds !== 'object' || !Object.keys(ds).length) {
    host.state.update({ error: 'empty' })
    return null
  }
  let mods = null
  try {
    mods = await Promise.all([
      import('@symbo-ls/workspace-design-system/components'),
      import('@symbo-ls/workspace-design-system/pages/dsPages.js'),
      import('@symbo-ls/workspace-design-system/functions/frameKit.js')
    ])
  } catch (_) {
    mods = null
  }
  if (host.__previewDsSig !== sig) return null
  if (!mods) {
    host.state.update({ error: 'fold' })
    return null
  }
  const [dsComponents, dsPagesMod, kit] = mods
  try {
    const out = await host.call(
      'embedDesignSystem',
      node,
      view.data,
      { dsPage: '/' + section },
      { ...dsComponents },
      {
        dsPages: dsPagesMod.default || dsPagesMod,
        dsFrameKit: kit.designSystemFrameKit(),
        el: host
      }
    )
    if (out && out.bootError) {
      host.state.update({ error: 'boot' })
      return null
    }
  } catch (_) {
    host.state.update({ error: 'boot' })
    return null
  }
  if (host.__previewDsSig !== sig) return null
  host.state.update({ ready: true })
  return section
}
